import React from "react";
import { Link } from "react-router-dom";
import logo from "../../assets/wlogo.png";

const ShieldIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 2l8 4v6c0 5-3.4 9.4-8 10-4.6-.6-8-5-8-10V6l8-4Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 12l2 2 4-5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TruckIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M3 7h11v10H3V7Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M14 10h4l3 3v4h-7v-7Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M6.5 19.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM17.5 19.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"
      fill="currentColor"
    />
  </svg>
);

const LabIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M10 2v6l-6 10a2 2 0 0 0 1.7 3h12.6a2 2 0 0 0 1.7-3L14 8V2"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 14h8"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

function Badge({ icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl bg-white/10 ring-1 ring-white/15 px-4 py-3">
      <div className="mt-0.5 text-white/90">{icon}</div>
      <div className="leading-tight">
        <div className="text-sm font-semibold text-white">{title}</div>
        <div className="text-xs text-white/75">{subtitle}</div>
      </div>
    </div>
  );
}

export default function Footer() {
  const scrollToTop = () => window.scrollTo(0, 0);

  return (
    <footer className="bg-blue-900 text-white">
      {/* Top strip */}
      <div className="border-b border-white/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Badge
              icon={<ShieldIcon className="w-12 h-12" />}
              title="Quality-first"
              subtitle="Strict sourcing, consistent standards, uncompromising control"
            />
            <Badge
              icon={<LabIcon className="w-12 h-12" />}
              title="Research-driven"
              subtitle="Designed around laboratory precision and scientific workflows"
            />
            <Badge
              icon={<TruckIcon className="w-12 h-12" />}
              title="Reliable fulfillment"
              subtitle="Fast processing, careful handling, discreet delivery"
            />
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-20">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" onClick={scrollToTop} className="inline-flex items-center">
              <img src={logo} alt="HD Labs" className="h-40 w-80 object-contain" />
            </Link>
          </div>

          {/* Links */}
          <div>
            <div className="text-sm font-semibold tracking-wide">
              <Link className="hover:text-white" to="/shop" onClick={scrollToTop}>
                All Products
              </Link>
            </div>
            <ul className="mt-4 space-y-2 text-sm text-white/80">
              <li>
                <Link className="hover:text-white" to="/shop?category=Peptides" onClick={scrollToTop}>
                  Peptides
                </Link>
              </li>
              <li>
                <Link
                  className="hover:text-white"
                  to="/shop?category=HGH%20%26%20IGF-1"
                  onClick={scrollToTop}
                >
                  HGH &amp; IGF-1
                </Link>
              </li>
              <li>
                <Link
                  className="hover:text-white"
                  to="/shop?category=Sexual%20aid"
                  onClick={scrollToTop}
                >
                  Sexual aid
                </Link>
              </li>
              <li>
                <Link
                  className="hover:text-white"
                  to="/shop?category=Post%20%26%20On%20cycle%20Therapy"
                  onClick={scrollToTop}
                >
                  Post &amp; On cycle Therapy
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <div className="text-sm font-semibold tracking-wide">Company</div>
            <ul className="mt-4 space-y-2 text-sm text-white/80">
              <li>
                <a className="hover:text-white" href="/#standards">
                  Standards
                </a>
              </li>
              <li>
                <a className="hover:text-white" href="/#reviews">
                  Reviews
                </a>
              </li>
              <li>
                <Link className="hover:text-white" to="/FAQ">
                  FAQ
                </Link>
              </li>
              <li>
                <Link className="hover:text-white" to="/contact">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact + notice */}
          <div>
            <div className="text-sm font-semibold tracking-wide">Support</div>
            <div className="mt-4 space-y-2 text-sm text-white/80">
              <a
                href="mailto:support@hdlabs.ca"
                className="text-white/80 text-sm hover:text-white underline underline-offset-4 decoration-white/30 hover:decoration-white transition"
              >
                support@hdlabs.ca
              </a>
              <div>
                <span className="text-white/60">Hours:</span>{" "}
                <span className="text-white/90">Mon–Fri</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-10 pt-6 border-t border-white/15 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between text-xs text-white/75">
          <div>© {new Date().getFullYear()} HD Labs</div>
        </div>
      </div>
    </footer>
  );
}